/**
 * Brand Bible v2 fold-in — human labels for entries, keyed by table.
 * Shared by the needs-attention queue and the inline enrichment-drafts panels
 * so both surfaces describe the same row the same way. Mirrors the per-table
 * label logic StepIdentity/StepVoice/etc. use in the wizard (contract Appendix
 * A payload shapes), generalized across every wizard-v1 table.
 */
import type { BbEntry, BbTable } from '@/features/brand-bible-wizard/types';

const fallback = (v: unknown): string => (v == null || v === '' ? '—' : String(v));

/** Short caps label for a row, e.g. "Legal name", "Service", "Compliance rule". */
export function bbEntryKindLabel(table: BbTable, entry: BbEntry): string {
  switch (table) {
    case 'identity':
      return 'Legal name';
    case 'voice_profile':
      return 'Voice summary';
    case 'services':
      return 'Service';
    case 'locations': {
      const e = entry as BbEntry<'locations'>;
      return `Location${e.metro ? ` · ${e.metro}` : ''}`;
    }
    case 'credentials': {
      const e = entry as BbEntry<'credentials'>;
      return e.kind ? e.kind[0].toUpperCase() + e.kind.slice(1) : 'Credential';
    }
    case 'offers': {
      const e = entry as BbEntry<'offers'>;
      return e.kind ? e.kind.replace(/_/g, ' ') : 'Offer';
    }
    case 'review_stats': {
      const e = entry as BbEntry<'review_stats'>;
      return `Review stat${e.platform ? ` · ${e.platform}` : ''}`;
    }
    case 'case_studies':
      return 'Case study';
    case 'compliance_rules': {
      const e = entry as BbEntry<'compliance_rules'>;
      return e.kind ? e.kind.replace(/_/g, ' ') : 'Compliance rule';
    }
    case 'visual_identity':
      return 'Visual identity';
    case 'chunks': {
      const e = entry as BbEntry<'chunks'>;
      return e.kind ? e.kind.replace(/_/g, ' ') : 'Chunk';
    }
    default:
      return table;
  }
}

/** Rendered value line for the row. */
export function bbEntryValue(table: BbTable, entry: BbEntry): string {
  switch (table) {
    case 'identity': {
      const e = entry as BbEntry<'identity'>;
      return fallback(e.legal_name ?? e.dba);
    }
    case 'voice_profile': {
      const e = entry as BbEntry<'voice_profile'>;
      return fallback(e.voice_summary);
    }
    case 'services': {
      const e = entry as BbEntry<'services'>;
      return fallback(e.name);
    }
    case 'locations': {
      const e = entry as BbEntry<'locations'>;
      return fallback(e.address ?? e.tracking_phone);
    }
    case 'credentials': {
      const e = entry as BbEntry<'credentials'>;
      return fallback(e.number ?? e.name);
    }
    case 'offers': {
      const e = entry as BbEntry<'offers'>;
      return fallback(e.title ?? e.verbatim_text);
    }
    case 'review_stats': {
      const e = entry as BbEntry<'review_stats'>;
      return e.rating != null ? `${e.rating} (${e.review_count ?? 0})` : fallback(e.review_count);
    }
    case 'case_studies': {
      const e = entry as BbEntry<'case_studies'>;
      return fallback(e.result ?? e.job_type);
    }
    case 'compliance_rules': {
      const e = entry as BbEntry<'compliance_rules'>;
      return fallback(e.rule_text);
    }
    case 'visual_identity':
      return 'Colors / logo';
    case 'chunks': {
      const e = entry as BbEntry<'chunks'>;
      return fallback(e.title ?? e.body);
    }
    default:
      return '—';
  }
}

const SOURCE_LABEL: Record<string, string> = {
  intake: 'intake',
  discovered: 'discovered',
  inferred: 'inferred',
  performance: 'performance',
  client_edit: 'client edit',
};

export function bbEntrySourceLabel(entry: BbEntry, siteLabel: string): string {
  if (entry.source === 'discovered') return `site · ${siteLabel}`;
  return SOURCE_LABEL[entry.source] ?? entry.source;
}
